// Dependencies
import React, { useEffect, useState, useRef, useCallback } from "react";

// Modules
import { useSelector } from "react-redux";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

// App
import { client, serverUrl, apiEndpoints } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { alertMessages } from "../../partials/alertMessages";
import Error from "../../partials/error";
import SkeletonSingleScreen from "../../partials/skeleton-screens/skeletonSingleScreen";
import { logCustomEvent } from "../../core/analytics";
import { favoritesContentType } from "../../utils/helpers";
import PostBodyEdit from "../../partials/posts/postBodyEdit";
import { patchNode } from "../../core/postNode";
import MediaLibrary from "../../partials/mediaLibrary";
import { get_csrf_token } from "../../core/auth";
import PostSingleActions from "../../partials/posts/postSingleActions";

// UI components
import SinglesHeader from "../../partials/singlesHeader";
import { Col, Row, Stack } from "react-bootstrap";
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
} from "@mui/material";

export default function PostEdit(props) {
  // Data

  const {
    apiEndpoint,
    screenClassName,
    categoryTitle,
    categoryName,
    targetVid,
    returnRoute,
    backBtnText,
    previewRoute,
    logCustomEventType,
    setEntityPost,
    extraFields,
  } = props;

  const [post, setPost] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [csrf_token, setCsrfToken] = useState(null);

  // Editable fields
  const [postTitle, setPostTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const [reactionsEnabled, setReactionsEnabled] = useState(true);
  const [commentsEnabled, setCommentsEnabled] = useState(true);

  // Status
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImageMid, setFeaturedImageMid] = useState(null);

  const user = useSelector((state) => state.authReducer.user);
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");
  const navigate = useNavigate();

  const queueReloadRef = useRef(false);

  useEffect(() => {
    get_csrf_token().then((response) => setCsrfToken(response.data));
    loadNode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!post) return; // skip on initial load
    const featuredImageData =
      props.featuredImageEnabled && featuredImageMid
        ? { field_featured_image: featuredImageMid }
        : {};
    debouncedUpdate({
      title: postTitle,
      body: editorContent,
      field_category: [selectedCategory],
      field_allow_reactions: reactionsEnabled,
      field_allow_comments: commentsEnabled,
      ...featuredImageData,
      ...extraFields,
    });
  }, [
    postTitle,
    editorContent,
    reactionsEnabled,
    commentsEnabled,
    selectedCategory,
    extraFields,
    featuredImageMid,
  ]);

  // ─── Data fetching ────────────────────────────────────────────────────────

  const loadNode = () => {
    const id = params.id;
    if (id) {
      getContent(id);
    } else {
      setError(true, 0, alertMessages.contentError);
    }
  };

  const spreadPostData = (data) => {
    if (setEntityPost) {
      setEntityPost(data);
    }
    setPost(data);
    setPostTitle(data.title);
    setEditorContent(data.body);
    setSelectedCategory(parseInt(data.category_ids));
    setReactionsEnabled(data.field_allow_reactions);
    setCommentsEnabled(data.field_allow_comments);
    if (props.featuredImageEnabled) {
      setFeaturedImage(data.field_featured_image);
    }
  };

  const getContent = (id) => {
    let path;

    if (client === "companyapp") {
      path = `${apiEndpoint}/${id}?_format=json`;
      if (hash) path += `&auHash=${hash}`;
    } else {
      path = `${apiEndpoint}/all/${id}?_format=json&status=1&promote=1`;
      if (hash) path = `/api/v2/shared?bundle=video&nid=${id}&authkey=${hash}`;
    }

    getOnlineNode(path, user.access_token)
      .then((response) => {
        if (response.status === 200) {
          setPost(response.data);
          spreadPostData(response.data);
          if (logCustomEventType) {
            logCustomEvent("read", response.data, logCustomEventType);
          }
          if (!initialData) setInitialData(response.data);
          setIsError(false);
        } else {
          setError(true, 404, "No data");
        }
      })
      .catch((_error) => {
        if (_error?.response?.status === 403) {
          setError(true, 403, "You do not have access to view this content.");
        } else {
          setError(true, 500, "Failed to get data");
        }
      })
      .finally(() => setIsLoading(false));
  };

  const setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);
    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  // ─── Actions ──────────────────────────────────────────────────────────────

  const debounceTimer = useRef(null);

  const debouncedUpdate = useCallback(
    (data) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        patchNode(`${apiEndpoint}/${post.id}`, data, csrf_token)
          .then((response) => {
            setIsSaving(false);
            if (queueReloadRef.current) {
              loadNode();
              queueReloadRef.current = false;
            }
          })
          .catch((error) => {
            console.error("save error", error);
            setIsSaving(false);
          });
      }, 800);
    },
    [post?.id, csrf_token],
  );

  const revertChanges = () => {
    if (!initialData) return;
    spreadPostData(initialData);
    setPost(initialData);
    setResetKey((prev) => prev + 1);
  };
  // ─── Render ───────────────────────────────────────────────────────────────

  if (isLoading) return <SkeletonSingleScreen />;
  if (isError) return <Error status={errorStatus} message={errorMessage} />;

  return (
    <main className={`${screenClassName} single post-edit`} data-id={post.id}>
      <article className="article">
        <SinglesHeader
          singleEdit={true}
          nestedCategory={true}
          categoryTitle={categoryTitle}
          categoryName={categoryName}
          selectedCategory={selectedCategory}
          setSelectedCategory={(value) => {
            setSelectedCategory(value);
          }}
          user={user}
          targetVid={targetVid}
          route={returnRoute}
          backBtnText={backBtnText}
          hash={hash}
        />

        <TextField
          className="post-title-edit"
          owner="client"
          variant="standard"
          value={postTitle}
          family="secondary"
          textvariant="h1"
          onInput={(e) => setPostTitle(e.target.value)}
        />
        {props.featuredImageEnabled && (
          <div className="post-featured-image">
            <Button
              variant="outlined"
              component="label"
              size="small"
              onClick={() => {
                setShowMediaLibrary(true);
              }}
            >
              <Typography owner="admin" variant="button">
                Select featured image
              </Typography>
            </Button>
            {featuredImage && <img src={`${serverUrl}${featuredImage}`} />}
            <MediaLibrary
              user={user}
              files={
                props.featuredImageFiles
                  ? props.featuredImageFiles
                  : {
                      aspectRatio: 1.778,
                    }
              }
              csrf_token={csrf_token}
              mediaType={"image"}
              showMediaLibrary={showMediaLibrary}
              setShowMediaLibrary={setShowMediaLibrary}
              onMediaInsert={(media) => {
                queueReloadRef.current = true;
                setFeaturedImageMid(media.fid);
                setShowMediaLibrary(false);
              }}
            />
          </div>
        )}
        {props.children}

        <PostBodyEdit
          user={user}
          csrf_token={csrf_token}
          key={resetKey}
          editorContent={editorContent}
          setEditorContent={setEditorContent}
          editorProps={props.editorProps}
        />

        <div className="form-group mb-3">
          {!props.disableReactionsToggle && (
            <Row className="gx-0">
              <Col xs="auto">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={reactionsEnabled}
                      onChange={(e) => setReactionsEnabled(e.target.checked)}
                    />
                  }
                  label={
                    <Typography
                      owner="admin"
                      variant="body2"
                      color="adminPrimary.main"
                      weight="medium"
                      lh="medium"
                    >
                      Allow reactions
                    </Typography>
                  }
                />
              </Col>
              <Col xs="auto">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={commentsEnabled}
                      onChange={(e) => setCommentsEnabled(e.target.checked)}
                    />
                  }
                  label={
                    <Typography
                      owner="admin"
                      variant="body2"
                      color="adminPrimary.main"
                      weight="medium"
                      lh="medium"
                    >
                      Allow comments
                    </Typography>
                  }
                />
              </Col>
            </Row>
          )}

          <PostSingleActions
            previewRoute={props.previewOverride ? props.previewOverride : `${previewRoute}/${post.id}`}
            revertChanges={revertChanges}
          />
        </div>
      </article>
    </main>
  );
}
